import { Row, Col } from "antd";
import { Fade } from "react-awesome-reveal";

import { SvgIcon } from "../../common/SvgIcon";
import { Button } from "../../common/Button";
import Container from "../../common/Container";
import ScrollToTop from "../../common/ScrollToTop";
import { useEffect, useState, useRef } from "react";
import styled from "styled-components";

const stats = [
  { label: "Trabajos realizados", value: 56522 },
  { label: "Años de experiencia", value: 22 },
];

const ServiceItem = styled.li`
  font-size: 2rem;
  line-height: 1.6;
  margin: 0.5rem 0;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  color: #2e186a;

  &:hover {
    transform: scale(1.1);
    font-weight: bold;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ModalOverlay = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  justify-content: center;
  align-items: center;
  z-index: 9999;
  overflow: auto; /* Permite hacer scroll si el contenido es alto */
  padding: 2rem;  /* Espacio para mobile */
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  max-width: 1000px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column; /* Column para poder tener scroll en body */
  overflow: hidden; /* Muy importante para que los bordes redondeados se mantengan */
  padding: 1rem;

  @media (max-width: 1200px) {
    width: 95%;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  overflow-y: auto; /* Scroll vertical solo aquí */
  max-height: 80vh; /* Altura máxima para scroll */
  padding: 2rem 0;

  img {
    border-radius: 8px;
    width: 300px;
    height: 300px;
    object-fit: cover;

    @media (max-width: 1200px) {
      width: 250px;  /* Más pequeña en pantallas medianas */
      height: 250px;
    }

    @media (max-width: 992px) {
      width: 200px;
      height: 200px;
    }

    @media (max-width: 768px) {
      width: 100%;  /* Full width en móviles */
      height: auto;
    }
  }

  div {
    flex: 1;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;


const CloseButton = styled.button`
  background: ${(p) => p.color || "#2e186a"};
  color: ${(p) => (p.color ? "#2E186A" : "#fff")};
  font-size: 1rem;
  font-weight: 700;
  border: none;
  padding: 0.5rem 1.5rem; /* más compacto */
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  align-self: flex-start; /* lo mantiene a la izquierda dentro del modal, opcional */

  &:hover,
  &:active,
  &:focus {
    color: #fff;
    border: 1px solid rgb(255, 130, 92);
    background-color: rgb(255, 130, 92);
  }
`;

export const Address = styled.p`
  transition: transform 0.3s ease-in-out;
  cursor: pointer;

  &:hover {
    transform: scale(1.1);
  }
`;

// Componente contador que se activa al entrar en pantalla
function CounterOnView({ end, duration = 2000 }: { end: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        clearInterval(timer);
        setCount(end);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [hasStarted, end, duration]);

  return (
    <span
      ref={ref}
      style={{
        display: "inline-block",
        transform: "scale(1)",
        animation: hasStarted ? "pop 0.5s ease" : "none",
      }}
    >
      {count}
    </span>
  );
}

// CSS de animación (puedes ponerlo en App.css o en un archivo global)
const popAnimationStyle = `
@keyframes pop {
  0% { transform: scale(0.5); opacity: 0.3; }
  100% { transform: scale(1); opacity: 1; }
}
`;
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.innerHTML = popAnimationStyle;
  document.head.appendChild(style);
}

const Home = () => {
  const scrollTo = (id: string) => {
    const element = document.getElementById(id) as HTMLDivElement;
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  interface ServiceBlock {
    type: "text" | "list";
    value: string | string[];
  }

  interface Service {
    title: string;
    image: string;
    content: ServiceBlock[];
  }

  useEffect(() => {
    services.forEach(service => {
      const img = new Image();
      img.src = service.image;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const services: Service[] = [
    {
      title: "Limpieza y mantenimiento de comunidades de vecinos",
      image: "/img/icons/comunidades.webp",
      content: [
        {
          type: "text",
          value:
            "El servicio de limpieza integral y mantenimiento de comunidades de vecinos o edificios, se adapta a las necesidades de los clientes para las zonas comunes de las instalaciones.",
        },
        {
          type: "text",
          value:
            "La desaparición de la figura del 'Portero' y la colaboración con las diferentes Administraciones de Fincas, permite que LIMPIEZAS KEVHEL ofrezca cada día sus servicios personalizados a nuestra cartera de clientes.",
        },
      ],
    },
    {
      title: "Limpieza de establecimientos comerciales y oficinas",
      image: "/img/icons/oficinas.webp",
      content: [
        {
          type: "text",
          value:
            "Los servicios de nuestra empresa garantizan a las oficinas y establecimientos comerciales un entorno de trabajo higiénico y organizado.",
        },
        {
          type: "text",
          value: "Las frecuencias de prestación del servicio pueden ser las elegidas por el cliente:",
        },
        {
          type: "list",
          value: [
            "Servicio diario de limpieza",
            "Limpiezas en días alternos",
          ],
        },
        {
          type: "text",
          value: "Tareas comunes en la limpieza de estas instalaciones:",
        },
        {
          type: "list",
          value: [
            "Limpieza de mobiliario y superficies",
            "Limpieza de suelos",
            "Gestión de residuos",
            "Desinfección de zonas",
            "Reposición de suministros en baños y office",
          ],
        },
      ],
    },
    {
      title: "Limpieza de garajes",
      image: "/img/icons/garajes.webp",
      content: [
        {
          type: "text",
          value:
            "Para realizar este servicio, contamos con máquinas barredoras-fregadoras con operarios a bordo, y otras maquinarias diseñadas para la limpieza efectiva de esstos espacios.",
        },
        {
          type: "text",
          value: "Realizamos el mantenimiento en todo tipo de parkings:",
        },
        {
          type: "list",
          value: [
            "Garajes de comunidades de vecinos",
            "Parkings de oficinas",
            "Parkings de edificios públicos",
          ],
        },
      ],
    },
    {
      title: "Limpieza de sociedades gastronómicas",
      image: "/img/icons/sociedades.webp",
      content: [
        {
          type: "text",
          value:
            "Atendemos solicitudes de limpieza programada a sociedades gastronómicas y establecimientos hosteleros, según frecuencias solicitadas por el cliente.",
        },
        {
          type: "text",
          value:
            "Nuestro servicio incluye posibilidad de limpiezas en fines de semana.",
        },
      ],
    },
    {
      title: "Limpieza de cristales",
      image: "/img/icons/cristales.webp",
      content: [
        {
          type: "text",
          value:
            "Prestamos a nuestros clientes el servicio de limpieza de cristales en frecuencias planificadas o servicio puntual, empleando para ello los productos adecuados para cada caso.",
        },
      ],
    },
    {
      title: "Tratamiento de suelos",
      image: "/img/icons/suelos.webp",
      content: [
        {
          type: "text",
          value:
            "Realizamos un análisis y una evaluación técnica inicial, para determinar ante qué superficie nos encontramos y cuál es el tratamiento más adecuado.",
        },
        {
          type: "list",
          value: [
            "Decapado",
            "Abrillantado",
            "Encerado de suelos de madera",
          ],
        },
      ],
    },
    {
      title: "Limpieza y eliminación de grafitis",
      image: "/img/icons/grafitis.webp",
      content: [
        {
          type: "text",
          value:
            "Eliminamos grafitis en fachadas de edificios, la efectividad de este servicio depende de la superficie a tratar.",
        },
      ],
    },
  ];



  const [selectedService, setSelectedService] = useState<Service | null>(null);

  return (
    <Container>
      <ScrollToTop />

      {/* Intro Section */}
      <section id="intro" style={{ padding: "4rem 0" }}>
        <Fade direction="right" triggerOnce>
          <Row justify="space-between" align="middle">
            <Col lg={12} md={11} sm={12} xs={24}>
              <h6>Kevhel Marema S.L.</h6>
              <p style={{ textAlign: "justify" }}>
                Somos una empresa española con presencia internacional y más de 20 años de experiencia.
                Ofrecemos servicios de limpieza profesional, 
                ofreciendo soluciones adaptadas a cada cliente.
              </p>
              <Button onClick={() => scrollTo("about")}>
                Descubre nuestros servicios
              </Button>
            </Col>
            <Col lg={11} md={11} sm={12} xs={24}>
              <SvgIcon src="IAkg.webp" width="100%" height="100%" />
            </Col>
          </Row>
        </Fade>
      </section>

      {/* About Section */}
      <section id="about" style={{ padding: "4rem 0", textAlign: "center" }}>
        <Fade triggerOnce>
          <h6 style={{ marginBottom: "0.8em" }}>Nuestros servicios</h6>
          <Row justify="center" align="middle" style={{ margin: 0, padding: 0 }}>
            <Col xs={24} sm={12} style={{ padding: 0 }}>
              <ul style={{ listStyle: "none", paddingLeft: 0, marginBottom: 0 }}>
                {services.slice(0, 3).map((item, index) => (
                  <ServiceItem key={index} onClick={() => setSelectedService(item)}>
                    {item.title}
                  </ServiceItem>
                ))}
              </ul>
            </Col>
            <Col xs={24} sm={12} style={{ padding: 0 }}>
              <ul style={{ listStyle: "none", paddingLeft: 0, marginBottom: 0 }}>
                {services.slice(3).map((item, index) => (
                  <ServiceItem key={index} onClick={() => setSelectedService(item)}>
                    {item.title}
                  </ServiceItem>
                ))}
              </ul>
            </Col>
          </Row>
        </Fade>
      </section>

      {/* Modal */}
      <ModalOverlay visible={!!selectedService}>
        {selectedService && (
          <ModalContent>
            <ModalBody>
              <img src={selectedService.image} alt={selectedService.title} />
              <div>
                <h4>{selectedService.title}</h4>

                {selectedService.content.map((block, idx) => {
                  if (block.type === "text" && typeof block.value === "string") {
                    return <p key={idx}>{block.value}</p>;
                  }
                  if (block.type === "list" && Array.isArray(block.value)) {
                    return (
                      <ul key={idx}>
                        {block.value.map((point, i) => (
                          <li key={i} style={{ color: "#2e186a" }}>{point}</li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })}
              </div>
            </ModalBody>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <CloseButton onClick={() => setSelectedService(null)}>Cerrar</CloseButton>
            </div>
          </ModalContent>
        )}
      </ModalOverlay>




      {/* Mission Section */}
      <section id="mission" style={{ padding: "4rem 0", textAlign: "center" }}>
        <Fade direction="up" triggerOnce>
          <Row justify="center" gutter={[40, 40]}>
            {stats.map((stat, index) => (
              <Col key={index} xs={24} sm={8}>
                <div
                  style={{
                    width: "160px",
                    height: "160px",
                    borderRadius: "50%",
                    backgroundColor: "#2e186a",
                    color: "#fff",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "0 auto 1rem",
                    fontSize: "2rem",
                    fontWeight: "bold",
                  }}
                >
                  <CounterOnView end={stat.value} />
                </div>
                <p>{stat.label}</p>
              </Col>
            ))}
          </Row>
        </Fade>
      </section>

      {/* Product Section */}
      <section id="product" style={{ padding: "4rem 0" }}>
        <Fade direction="left" triggerOnce>
          <Row justify="space-between" align="middle">
            <Col
              lg={13} md={11} sm={12} xs={24}
              style={{ marginRight: "2rem" }} // Añadido margen derecho
            >
              <div style={{ width: "100%", height: "400px", overflow: "hidden", borderRadius: "12px" }}>
                <iframe
                  src="https://www.google.com/maps/d/u/0/embed?mid=1gLphNF5V0-AvRlhD0C4OFAPsQ24oxk0&ehbc=2E312F"
                  width="100%"
                  height="550"
                  title="Mapa de sedes"/* un poco más alto de lo necesario */
                  style={{ border: "0", marginTop: "-60px" }} /* desplazamos hacia arriba */
                ></iframe>
              </div>

            </Col>
            <Col lg={10} md={11} sm={12} xs={24}> {/* Ajuste del ancho para balancear */}
              <h6>Encuéntranos</h6>
              <Address>Calle Mayor, 15bj, Burlada (31600), Navarra</Address>
              <Address>Calle Iturrama, 5bj, Pamplona (31007), Navarra</Address>
              <Address>Avenida de Orihuela 90, Alicante (03006), Comunidad Valenciana</Address>
              <Address>Calle Dos Bocas 6416, Ciudad de Panamá, Panamá</Address>
            </Col>
          </Row>
        </Fade>
      </section>


      {/* Contact Section */}
      <section id="contact" style={{ padding: "4rem 0" }}>
        <Fade direction="right" triggerOnce>
          <Row justify="center" align="middle">
            <Col lg={12} md={12} sm={24} xs={24}>
              <h6>Contacto</h6>
              <p>
                Para solicitar presupuestos o cualquier tipo de información, no dudes en llamarnos o enviar un correo.
                Nuestro personal estará encantado de contactarle.
              </p>
              <p>
                <strong>Teléfonos:</strong> <br />
                <span className="hoverScale">948 06 99 22</span><br />
                <span className="hoverScale">675 59 98 17</span><br />
                <span className="hoverScale">622 95 69 15</span>
              </p>
              <p>
                <strong>Correos:</strong> <br />
                <span className="hoverScale">dptotecnico@kevhel.com</span><br />
                <span className="hoverScale">administracion@kevhel.com</span>
              </p>
            </Col>
            <Col lg={11} md={11} sm={12} xs={24}>
              <SvgIcon src="kgfoto.webp" width="100%" height="100%" />
            </Col>
          </Row>
        </Fade>

        {/* CSS inline global para el hover */}
        <style>{`
          .hoverScale {
            display: inline-block;
            transition: transform 0.2s ease-in-out;
            cursor: pointer;
          }
          .hoverScale:hover {
            transform: scale(1.1);
          }
        `}</style>
      </section>
    </Container>
  );
};

export default Home;